import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../lib/cn.ts';

/**
 * Tactile button. Press animation uses scale + translate (transform-only,
 * hardware-accelerated). No gradient fills. Accent variant is the only one
 * that uses the Caution Tape yellow — keep usage thin per token guidance.
 */
const buttonStyles = cva(
  [
    'relative inline-flex select-none items-center justify-center gap-2',
    'whitespace-nowrap font-medium tracking-tight',
    'rounded-md outline-none',
    'transition-[background-color,color,box-shadow,transform] duration-quick ease-out-expo',
    'focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-bg-base',
    'active:translate-y-px active:scale-[0.985]',
    'disabled:pointer-events-none disabled:opacity-50',
  ],
  {
    variants: {
      variant: {
        accent: [
          'bg-accent text-accent-fg shadow-rest',
          'hover:bg-[rgb(var(--accent-strong))]',
        ],
        primary: [
          'bg-bg-raised text-fg shadow-rest',
          'hover:bg-[rgb(var(--bg-overlay))] hover:shadow-raised',
        ],
        ghost: [
          'bg-transparent text-fg-muted',
          'hover:bg-bg-raised hover:text-fg',
        ],
        outline: [
          'bg-transparent text-fg shadow-flat',
          'hover:bg-bg-raised',
        ],
        danger: [
          'bg-danger/95 text-fg shadow-rest',
          'hover:bg-danger',
        ],
      },
      size: {
        sm: 'h-8 px-3 text-xs',
        md: 'h-10 px-4 text-sm',
        lg: 'h-12 px-5 text-base',
        icon: 'h-10 w-10 text-base',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonStyles> {
  asChild?: boolean;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp ref={ref} className={cn(buttonStyles({ variant, size }), className)} {...props} />
    );
  },
);
Button.displayName = 'Button';

export { buttonStyles };
